/**
 * Organizations domain — API types and fetch helpers.
 *
 * All fetch calls use credentials: "include" so the browser forwards the
 * HttpOnly session cookie automatically. Never read or manipulate the token.
 *
 * Routes:
 *   GET    /api/organizations
 *   POST   /api/organizations
 *   GET    /api/organizations/{id}
 *   PATCH  /api/organizations/{id}
 *   DELETE /api/organizations/{id}
 *   POST   /api/organizations/{id}/members
 *   DELETE /api/organizations/{id}/members/{user_id}
 *   PATCH  /api/organizations/{id}/members/{user_id}
 */

// On the server (SSR / Server Components) use the internal backend URL directly.
// In the browser use a relative path so the request goes through the Next.js
// rewrite proxy — same origin, no CORS.
const API_URL =
  typeof window === "undefined"
    ? (process.env.BACKEND_INTERNAL_URL ?? "http://localhost:8000")
    : "";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Organization {
  id: string;
  name: string;
  slug: string;
  created_at: string;
  member_count: number;
  status?: "active" | "suspended";
  updated_at?: string | null;
  assignment_status?: "active" | "invited" | "none" | null;
  whatsapp_status?: "active" | "inactive" | "error" | "not_configured" | null;
  whatsapp_phone_number_id_masked?: string | null;
  whatsapp_waba_id_masked?: string | null;
  onboarding_status?: "completed" | "pending" | "dismissed" | null;
  assigned_user?: {
    id: string;
    email: string;
    first_name?: string | null;
    last_name?: string | null;
    status: string;
    is_admin?: boolean;
    is_support?: boolean;
    last_login_at?: string | null;
  } | null;
}

export interface OrganizationCreate {
  name: string;
  slug?: string;
}

export interface OrganizationUpdate {
  name?: string;
  slug?: string;
}

export interface ClinicUserOnboard {
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  /** El idioma del usuario invitado: el del correo y el del panel. */
  language_preference?: "es" | "en" | "de" | null;
}

export interface OrganizationOnboard {
  name: string;
  slug: string;
  /** La ficha de Google Maps del local, si ya se tiene: el asistente la encuentra puesta. */
  google_maps_url?: string | null;
  clinic_user?: ClinicUserOnboard | null;
}

export interface OrganizationAdminSummary {
  organization: {
    id: string;
    name: string;
    slug: string;
    status: "active" | "suspended";
    created_at: string;
    updated_at: string;
  };
  assignment: {
    membership_id: string;
    role: string;
    membership_status: string;
    user: {
      id: string;
      email: string;
      first_name?: string | null;
      last_name?: string | null;
      status: string;
      is_admin: boolean;
      is_support: boolean;
      last_login_at?: string | null;
    };
    invitation?: {
      invitation_id: string;
      email: string;
      org_id: string;
      org_name: string;
      expires_at: string;
    } | null;
  } | null;
  whatsapp: {
    status: "active" | "inactive" | "error" | "not_configured";
    phone_number_id_masked?: string | null;
    waba_id_masked?: string | null;
  };
  can_invite: boolean;
  can_revoke: boolean;
  can_suspend: boolean;
  can_restore: boolean;
}

export interface OrganizationOnboardResponse {
  organization: OrganizationAdminSummary["organization"];
  assignment_result: "invitation_sent" | "assignment_created" | "none";
  assignment: OrganizationAdminSummary["assignment"];
}

export interface OrganizationSelfBootstrapResponse {
  organization: AccessibleOrganization;
}

export interface OrganizationSlugAvailability {
  slug: string;
  available: boolean;
}

export interface OrganizationListResponse {
  items: Organization[];
  total: number;
  skip: number;
  limit: number;
}

export interface OrganizationAccessInvitePayload {
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  language_preference?: "es" | "en" | "de" | null;
}

export interface MoveUserRequest {
  target_org_id: string;
}

// Re-export the MembershipResponse type shape used in org-member endpoints.
// The backend returns MembershipResponse for add/move member operations.
export interface OrgMembershipResponse {
  id: string;
  user_id: string;
  organization_id: string;
  role: "user" | "admin" | "owner";
  status: "active" | "revoked";
  created_at: string;
  updated_at: string;
}

/**
 * Extended organization record returned by GET /api/organizations/user/{userId}.
 * Includes the user's role and the membership_id needed for revocation.
 */
export interface OrganizationWithRole {
  id: string;
  name: string;
  slug: string;
  role: "user" | "admin" | "owner";
  membership_id: string;
}

export interface AccessibleOrganization {
  id: string;
  name: string;
  slug: string;
  role: "user" | "admin" | "owner";
}

interface ApiError {
  detail?: string;
  error?: { error_code?: string; message?: string };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function url(path: string): string {
  return `${API_URL}${path}`;
}

function authHeaders(cookieHeader?: string): HeadersInit {
  const headers: HeadersInit = { "Content-Type": "application/json" };
  if (cookieHeader) {
    headers["Cookie"] = cookieHeader;
  }
  return headers;
}

// ---------------------------------------------------------------------------
// API helpers — Server Component safe (accept cookieHeader)
// ---------------------------------------------------------------------------

/**
 * GET /api/organizations?skip=&limit=
 *
 * Lists all active organizations. Superadmin only.
 * Throws on error when called from a Server Component.
 */
export async function listOrganizations(
  skip = 0,
  limit = 50,
  cookieHeader?: string,
  options?: { search?: string; status?: string }
): Promise<OrganizationListResponse> {
  const params = new URLSearchParams({
    skip: String(skip),
    limit: String(limit),
  });
  if (options?.search) params.set("search", options.search);
  if (options?.status) params.set("status", options.status);
  const res = await fetch(url(`/api/organizations?${params.toString()}`), {
    method: "GET",
    credentials: "include",
    headers: authHeaders(cookieHeader),
    cache: "no-store",
  });
  if (!res.ok) {
    throw new Error(`No se pudieron cargar las organizaciones: ${res.status}`);
  }
  return (await res.json()) as OrganizationListResponse;
}

export async function checkOrganizationSlugAvailability(
  slug: string,
  cookieHeader?: string
): Promise<OrganizationSlugAvailability | null> {
  try {
    const params = new URLSearchParams({ slug });
    const res = await fetch(url(`/api/organizations/slug-availability?${params.toString()}`), {
      method: "GET",
      credentials: "include",
      headers: authHeaders(cookieHeader),
      cache: "no-store",
    });
    if (!res.ok) return null;
    return (await res.json()) as OrganizationSlugAvailability;
  } catch {
    return null;
  }
}

export async function createOrganizationOnboard(
  payload: OrganizationOnboard
): Promise<{ data: OrganizationOnboardResponse } | { error: string; body: ApiError }> {
  try {
    const res = await fetch(url("/api/organizations/onboard"), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      return { data: (await res.json()) as OrganizationOnboardResponse };
    }
    const body = (await res.json().catch(() => ({}))) as ApiError;
    return { error: body.detail ?? "No se pudo crear la organización.", body };
  } catch {
    return { error: "No se pudo conectar. Revisa tu conexión.", body: {} };
  }
}

export async function getOrganizationAdminSummary(
  id: string,
  cookieHeader?: string
): Promise<OrganizationAdminSummary | null> {
  try {
    const res = await fetch(url(`/api/organizations/${id}/admin-summary`), {
      method: "GET",
      credentials: "include",
      headers: authHeaders(cookieHeader),
      cache: "no-store",
    });
    if (!res.ok) return null;
    return (await res.json()) as OrganizationAdminSummary;
  } catch {
    return null;
  }
}

export async function restoreOrganization(
  id: string
): Promise<{ data: { id: string; status: "active" } } | { error: string; body: ApiError }> {
  try {
    const res = await fetch(url(`/api/organizations/${id}/restore`), {
      method: "POST",
      credentials: "include",
    });
    if (res.ok) {
      return { data: (await res.json()) as { id: string; status: "active" } };
    }
    const body = (await res.json().catch(() => ({}))) as ApiError;
    return { error: body.detail ?? "No se pudo restaurar la organización.", body };
  } catch {
    return { error: "No se pudo conectar. Revisa tu conexión.", body: {} };
  }
}

export async function inviteClinicUser(
  orgId: string,
  payload: OrganizationAccessInvitePayload
): Promise<{ data: OrganizationAdminSummary } | { error: string; body: ApiError }> {
  try {
    const res = await fetch(url(`/api/organizations/${orgId}/clinic-user/invite`), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      return { data: (await res.json()) as OrganizationAdminSummary };
    }
    const body = (await res.json().catch(() => ({}))) as ApiError;
    return { error: body.detail ?? "No se pudo invitar al usuario del local.", body };
  } catch {
    return { error: "No se pudo conectar. Revisa tu conexión.", body: {} };
  }
}

/**
 * GET /api/organizations/{id}
 *
 * Returns an organization by ID. Superadmin or active org member.
 * Returns null if not found (404); throws on other errors.
 */
export async function getOrganization(
  id: string,
  cookieHeader?: string
): Promise<Organization | null> {
  const res = await fetch(url(`/api/organizations/${id}`), {
    method: "GET",
    credentials: "include",
    headers: authHeaders(cookieHeader),
    cache: "no-store",
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`No se pudo cargar la organización: ${res.status}`);
  }
  return (await res.json()) as Organization;
}

/**
 * GET /api/organizations/me
 *
 * Returns the organizations accessible to the current authenticated user.
 * Regular users only receive their active memberships; superadmins receive all
 * active organizations with an effective elevated role.
 */
export async function listAccessibleOrganizations(
  cookieHeader?: string
): Promise<AccessibleOrganization[]> {
  try {
    const res = await fetch(url("/api/organizations/me"), {
      method: "GET",
      credentials: "include",
      headers: authHeaders(cookieHeader),
      cache: "no-store",
    });
    if (!res.ok) return [];
    return (await res.json()) as AccessibleOrganization[];
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// API helpers — Client-only mutations (return discriminated union)
// ---------------------------------------------------------------------------

/**
 * POST /api/organizations
 *
 * Creates a new organization. Superadmin only.
 */
export async function createOrganization(
  data: OrganizationCreate
): Promise<{ data: Organization } | { error: string; body: ApiError }> {
  try {
    const res = await fetch(url("/api/organizations"), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });

    if (res.ok) {
      return { data: (await res.json()) as Organization };
    }

    const body = (await res.json().catch(() => ({}))) as ApiError;
    return { error: body.detail ?? "No se pudo crear la organización.", body };
  } catch {
    return {
      error: "No se pudo conectar. Revisa tu conexión.",
      body: {},
    };
  }
}

export async function bootstrapSelfOrganization(
  data: OrganizationCreate
): Promise<{ data: OrganizationSelfBootstrapResponse } | { error: string; body: ApiError }> {
  try {
    const res = await fetch(url("/api/organizations/bootstrap-self"), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });

    if (res.ok) {
      return { data: (await res.json()) as OrganizationSelfBootstrapResponse };
    }

    const body = (await res.json().catch(() => ({}))) as ApiError;
    return { error: body.detail ?? "No se pudo crear el local inicial.", body };
  } catch {
    return {
      error: "No se pudo conectar. Revisa tu conexión.",
      body: {},
    };
  }
}

/**
 * PATCH /api/organizations/{id}
 *
 * Updates name and/or slug of an existing organization. Superadmin only.
 */
export async function updateOrganization(
  id: string,
  data: OrganizationUpdate
): Promise<{ data: Organization } | { error: string; body: ApiError }> {
  try {
    const res = await fetch(url(`/api/organizations/${id}`), {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });

    if (res.ok) {
      return { data: (await res.json()) as Organization };
    }

    const body = (await res.json().catch(() => ({}))) as ApiError;
    return { error: body.detail ?? "No se pudo actualizar la organización.", body };
  } catch {
    return {
      error: "No se pudo conectar. Revisa tu conexión.",
      body: {},
    };
  }
}

/**
 * DELETE /api/organizations/{id}
 *
 * Soft-deletes an organization. Superadmin only.
 * Returns 422 if this is the last active organization.
 */
export async function deleteOrganization(
  id: string
): Promise<{ ok: true } | { error: string; body: ApiError }> {
  try {
    const res = await fetch(url(`/api/organizations/${id}`), {
      method: "DELETE",
      credentials: "include",
    });

    if (res.status === 204) return { ok: true };

    const body = (await res.json().catch(() => ({}))) as ApiError;
    return { error: body.detail ?? "No se pudo eliminar la organización.", body };
  } catch {
    return {
      error: "No se pudo conectar. Revisa tu conexión.",
      body: {},
    };
  }
}

/**
 * POST /api/organizations/{orgId}/members
 *
 * Adds a user to an organization. Superadmin only.
 * Role must be "admin" or "member" (not "owner").
 */
export async function addUserToOrg(
  orgId: string,
  userId: string,
  role: "admin" | "member"
): Promise<
  { data: OrgMembershipResponse } | { error: string; body: ApiError }
> {
  try {
    const res = await fetch(url(`/api/organizations/${orgId}/members`), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId, role: role === "member" ? "user" : role }),
    });

    if (res.ok) {
      return { data: (await res.json()) as OrgMembershipResponse };
    }

    const body = (await res.json().catch(() => ({}))) as ApiError;
    return { error: body.detail ?? "No se pudo añadir el miembro.", body };
  } catch {
    return {
      error: "No se pudo conectar. Revisa tu conexión.",
      body: {},
    };
  }
}

/**
 * DELETE /api/organizations/{orgId}/members/{userId}
 *
 * Removes (revokes) a user's membership in an organization. Superadmin only.
 */
export async function removeUserFromOrg(
  orgId: string,
  userId: string
): Promise<{ ok: true } | { error: string; body: ApiError }> {
  try {
    const res = await fetch(
      url(`/api/organizations/${orgId}/members/${userId}`),
      {
        method: "DELETE",
        credentials: "include",
      }
    );

    if (res.status === 204) return { ok: true };

    const body = (await res.json().catch(() => ({}))) as ApiError;
    return { error: body.detail ?? "No se pudo eliminar el miembro.", body };
  } catch {
    return {
      error: "No se pudo conectar. Revisa tu conexión.",
      body: {},
    };
  }
}

/**
 * GET /api/organizations/user/{userId}
 *
 * Returns all organizations the given user belongs to, including their role
 * and membership_id in each org. Superadmin only.
 * Safe to call from Server Components (pass cookieHeader).
 * Returns empty array on error.
 */
export async function getUserOrganizations(
  userId: string,
  cookieHeader?: string
): Promise<OrganizationWithRole[]> {
  try {
    const res = await fetch(url(`/api/organizations/user/${userId}`), {
      method: "GET",
      credentials: "include",
      headers: authHeaders(cookieHeader),
      cache: "no-store",
    });
    if (!res.ok) return [];
    return (await res.json()) as OrganizationWithRole[];
  } catch {
    return [];
  }
}

/**
 * PATCH /api/organizations/{orgId}/members/{userId}
 *
 * Moves a user from their current org to targetOrgId. Superadmin only.
 * Returns the new membership in the target org.
 */
export async function moveUserToOrg(
  orgId: string,
  userId: string,
  targetOrgId: string
): Promise<
  { data: OrgMembershipResponse } | { error: string; body: ApiError }
> {
  try {
    const res = await fetch(
      url(`/api/organizations/${orgId}/members/${userId}`),
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ target_org_id: targetOrgId }),
      }
    );

    if (res.ok) {
      return { data: (await res.json()) as OrgMembershipResponse };
    }

    const body = (await res.json().catch(() => ({}))) as ApiError;
    return { error: body.detail ?? "No se pudo mover el miembro.", body };
  } catch {
    return {
      error: "No se pudo conectar. Revisa tu conexión.",
      body: {},
    };
  }
}
